import { useEffect, useRef, useState } from 'react';

import cloneDeep from 'lodash.clonedeep';

type SetStateFunction<T> = (prevState: T) => Partial<T>;
type StateUpdater<T> = (newState: Partial<T> | SetStateFunction<T>, callback?: (state: T) => void) => void;
type ResetFunction = (callback?: (state: any) => void) => void;

/**
 * React Hook reminiscent of React Class setState method with a few extra enhancements
 *
 * Like the useState hook, the first destructured value of this slice is the current state
 *
 * Second destructured item is a function for updating the state, but also takes a callback as a second
 * argument that is invoked after state is updated.
 *
 * Third function resets the state to its original value.
 *
 * @param initialValue - The initial state value
 * @returns [state, setState, resetState]
 */
export function useSuperState<T>(initialValue: T): [T, StateUpdater<T>, ResetFunction] {

    // Master state and setState functions
    const [state, setState] = useState<T>(initialValue);

    // Create references to the callback, initial value and shallow copy of the state
    const initialRef = useRef<T>();
    const callbacksRef = useRef<Array<(state: T) => void>>([]);

    // This is necessary to prevent updated state from being used in resetSuperState.
    // Why? Who knows, it's one of life's great mysteries.
    useEffect(() => {

        initialRef.current = cloneDeep(initialValue);

    }, [initialValue]);

    // Listen for state updates and broadcast to our callbacks
    useEffect(() => {

        callbacksRef.current.forEach(callback => callback(state));
        callbacksRef.current = [];

    }, [state]);

    if (typeof initialValue !== 'object' || Array.isArray(initialValue) || !initialValue) {

        return [state, setState as StateUpdater<T>, () => {}];

    }

    // Allow the state to be passed in piecemeal or entirety
    const setSuperState: StateUpdater<T> = (newState, callback) => {

        // If using a state function, pass a copy of state as a parameter for manipulation
        if (typeof newState === 'function') {

            newState = (newState as SetStateFunction<T>)(state);

        }

        // Apply the new values to the existing state object
        Object.keys(newState).forEach(key => {

            (state as any)[key] = (newState as any)[key];

        });

        if (typeof callback === 'function') {

            callbacksRef.current.push(callback);

        }

        // Set the actual state in order to trigger the re-render
        return setState(prevState => ({ ...prevState, ...newState }));

    };

    const resetSuperState: ResetFunction = callback => {

        if (typeof callback === 'function') {

            callbacksRef.current.push(callback);

        }

        setState(initialRef.current as T);

    };

    return [state, setSuperState, resetSuperState];

}

export default useSuperState;